import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import {
  TUpdateSpaceValidation,
  UpdateSpaceValidationSchema,
  useUpdateSpaceMutation,
} from '../../../api/update-space.api';
import { useCustomToast } from '@/hooks/use-custom-toast';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_SPACE_ADMIN_QUERY_KEY } from '../../../api/admin/get-list-space-admin.api';
import { GET_LIST_SPACES_QUERY_KEY } from '../../../api/get-list-space.api';
import { GET_SINGLE_SPACE_QUERY_KEY } from '../../../api/get-single-space.api';
import { useFileUpload } from '@/hooks/use-file-upload';
import { useTicketingModalRootStore } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';

const TOAST_MESSAGES_UPDATE_SPACE = {
  UPDATE_SPACE_ERROR: 'Error updating space, please try again!',
  UPDATE_SPACE_SUCCESS: 'Space updated successfully',
};

function buildUpdateSpacePayload(
  data: TUpdateSpaceValidation,
  initial: Pick<IUseUpdateSpaceModalProps, 'spaceId' | 'name' | 'description'>
): TUpdateSpaceValidation {
  const payload: TUpdateSpaceValidation = {
    spaceId: data?.spaceId ?? initial?.spaceId ?? '',
  };

  const initialName = initial?.name ?? '';
  const initialDescription = initial?.description ?? '';

  if (data?.name !== undefined && data.name !== initialName) {
    payload.name = data.name;
  }
  if (data?.description !== undefined && data.description !== initialDescription) {
    payload.description = data.description;
  }
  if (data?.icon instanceof File) {
    payload.icon = data.icon;
  }

  return payload;
}

export interface IUseUpdateSpaceModalProps {
  spaceId: string;
  name?: string | null;
  description?: string | null;
  icon?: string | null;
}

const useUpdateSpaceModal = (props: IUseUpdateSpaceModalProps) => {
  // HOOKS
  const showToast = useCustomToast();
  const { actions: modalActions } = useTicketingModalRootStore();

  // ----- Form -----
  const form = useForm<TUpdateSpaceValidation>({
    resolver: zodResolver(UpdateSpaceValidationSchema),
    mode: 'onChange',
  });

  // ----- Mutation -----
  const mutationUpdateSpace = useUpdateSpaceMutation({
    mutationConfig: {
      onError: (_error) => {
        // console.log('ERROR UPDATE SPACE=>', _error);
        const errorMessage = axiosExtractErrorMessage(_error) || TOAST_MESSAGES_UPDATE_SPACE.UPDATE_SPACE_ERROR;

        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: (_data) => {
        // console.log('SUCCESS UPDATE SPACE=>', _data);
        // Invalidate queries
        Promise.all([
          queryClient.invalidateQueries({ queryKey: [GET_LIST_SPACE_ADMIN_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [GET_LIST_SPACES_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [GET_SINGLE_SPACE_QUERY_KEY, { spaceId: props?.spaceId || '' }] }),
        ]);

        modalActions.closeModal('UpdateSpaceModal');
        form.reset();
        showToast({
          message: TOAST_MESSAGES_UPDATE_SPACE.UPDATE_SPACE_SUCCESS,
          type: 'success',
        });
      },
    },
  });

  // HELPERS
  const isOpenUpdateSpaceModal = useMemo(() => {
    return modalActions.checkIsOpenModal('UpdateSpaceModal');
  }, [modalActions]);

  // ----- File upload for icon -----
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);

  const {
    handleFileChange,
    clearFile,
    error: fileError,
  } = useFileUpload({
    acceptedFileTypes: [
      '.png',
      '.webp',
      '.jpg',
      '.jpeg',
      '.gif',
      '.svg',
      'image/png',
      'image/jpeg',
      'image/gif',
      'image/svg+xml',
    ],
    maxFileSize: 5 * 1024 * 1024, // 5MB
    onFileSelect: (file) => {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setIconPreview(previewUrl);
      // Set the file in the form
      form.setValue('icon', file);
    },
    onError: (_error) => {
      // Error is handled by the hook and displayed via fileError
    },
  });

  // EFFECTS
  useEffect(() => {
    if (props) {
      form.reset({
        spaceId: props?.spaceId || '',
        name: props?.name || '',
        description: props?.description || '',
        icon: undefined, // Don't set icon from props, handle separately
      });
      // Set initial icon preview if icon exists
      if (props?.icon && props.icon !== 'null') {
        setIconPreview(props.icon);
      } else {
        setIconPreview(null);
      }
    }
  }, [props, form]);

  // Clear preview when modal closes
  useEffect(() => {
    if (!isOpenUpdateSpaceModal) {
      clearFile();
      setIconPreview(null);
      form.setValue('icon', undefined);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpenUpdateSpaceModal, clearFile, form]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (iconPreview && iconPreview.startsWith('blob:')) {
        URL.revokeObjectURL(iconPreview);
      }
    };
  }, [iconPreview]);

  const isDisabledButton = useMemo(() => {
    return mutationUpdateSpace.isPending || form.formState.isSubmitting;
  }, [mutationUpdateSpace.isPending, form.formState.isSubmitting]);

  const isLoadingButton = useMemo(() => {
    return mutationUpdateSpace.isPending || form.formState.isSubmitting;
  }, [mutationUpdateSpace.isPending, form.formState.isSubmitting]);

  // HANDLERS
  const handleCloseModal = useCallback(() => {
    modalActions.closeModal('UpdateSpaceModal');
  }, [modalActions]);

  const handleSubmitForm = useCallback(
    (data: TUpdateSpaceValidation) => {
      const payload = buildUpdateSpacePayload(data, props);
      mutationUpdateSpace.mutate(payload);
    },
    [mutationUpdateSpace, props]
  );

  const handleIconClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemoveIcon = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      clearFile();
      setIconPreview(null);
      form.setValue('icon', undefined);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [clearFile, form]
  );

  const handleClose = useCallback(() => {
    handleCloseModal();
    form.reset();
  }, [handleCloseModal, form]);

  // RETURN
  return {
    // Form
    form,

    // Modal
    isOpenUpdateSpaceModal,
    isDisabledButton,
    isLoadingButton,

    // File upload
    fileInputRef,
    iconPreview,
    fileError,
    handleFileChange,
    handleIconClick,
    handleRemoveIcon,

    // Handlers
    handleCloseModal,
    handleSubmitForm,
    handleClose,
  };
};

export default useUpdateSpaceModal;
